<template>
  <Transition name="realtime-notice">
    <aside
      class="realtime-auth-notice"
      v-if="notice"
      :key="notice.event_id"
      role="status"
      aria-live="polite"
    >
      <span class="realtime-auth-icon" aria-hidden="true">
        <i class="material-icons">notifications_active</i>
      </span>
      <span class="realtime-auth-copy">
        <small>Authorization update</small>
        <strong>{{ notice.message }}</strong>
      </span>
      <button type="button" class="realtime-auth-open" :title="notice.action_label || 'View'"
        :aria-label="notice.action_label || 'View authorization update'" @click="openWorkspace" v-if="notice.action">
        <span>{{ notice.action_label || "View" }}</span>
        <i class="material-icons" aria-hidden="true">arrow_forward</i>
      </button>
      <button
        type="button"
        class="realtime-auth-sound"
        :title="soundEnabled ? 'Mute alert sounds' : 'Enable alert sounds'"
        :aria-label="soundEnabled ? 'Mute authorization alert sounds' : 'Enable authorization alert sounds'"
        :aria-pressed="!soundEnabled"
        @click="toggleSound"
        v-if="isHmo || isProvider"
      >
        <i class="material-icons" aria-hidden="true">{{ soundEnabled ? "volume_up" : "volume_off" }}</i>
      </button>
      <button
        type="button"
        class="realtime-auth-close"
        title="Dismiss"
        aria-label="Dismiss authorization update"
        @click="dismiss"
      >
        <i class="material-icons" aria-hidden="true">close</i>
      </button>
    </aside>
  </Transition>
</template>

<script>
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  dismissAuthorizationNotice,
  dismissPersistentAuthorizationNotice,
  handleAuthorizationNoticeAction,
} from "@/composables/authorizationRealtimeCoordinator";
import {
  initializeRealtimeAlertSound,
  realtimeAlertSoundEnabled,
  setRealtimeAlertSoundEnabled,
} from "@/composables/realtimeAlertSound";

export default {
  name: "RealtimeAuthNotice",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const notice = computed(() => store.state.realtimeAuthNotice);
    const isHmo = computed(() => store.state.user?.type === "hmo");
    const isProvider = computed(() => store.state.user?.type === "provider");
    const soundEnabled = realtimeAlertSoundEnabled;
    let stopSoundUnlock = null;

    const dismiss = () => {
      dismissAuthorizationNotice(store, notice.value?.event_id);
    };

    const openWorkspace = () => {
      const current = notice.value;
      if (!current) return;

      handleAuthorizationNoticeAction({ store, router, notice: current });
    };

    const toggleSound = () => {
      setRealtimeAlertSoundEnabled(!soundEnabled.value);
    };

    onMounted(() => {
      stopSoundUnlock = initializeRealtimeAlertSound();
    });

    onUnmounted(() => {
      stopSoundUnlock?.();
    });

    watch(
      () => route.fullPath,
      (nextPath, previousPath) => {
        if (nextPath !== previousPath) {
          dismissPersistentAuthorizationNotice(store);
        }
      }
    );

    return { notice, isHmo, isProvider, soundEnabled, dismiss, openWorkspace, toggleSound };
  },
};
</script>

<style scoped>
.realtime-auth-notice {
  align-items: center;
  background: #ffffff;
  border: 1px solid #9fcfc8;
  border-radius: 6px;
  box-shadow: 0 16px 34px -24px rgba(38, 50, 56, 0.8),
    0 6px 16px -12px rgba(0, 121, 107, 0.45);
  color: #263238;
  display: flex;
  gap: 12px;
  left: 50%;
  max-width: calc(100vw - 32px);
  padding: 10px;
  position: fixed;
  top: 84px;
  transform: translateX(-50%);
  width: min(620px, calc(100vw - 32px));
  z-index: 1500;
}

.realtime-auth-icon {
  align-items: center;
  background: #e8f5f3;
  border: 1px solid #c5e5e0;
  border-radius: 4px;
  color: #00897b;
  display: inline-flex;
  flex: 0 0 40px;
  height: 40px;
  justify-content: center;
}

.realtime-auth-icon i {
  animation: realtime-bell-arrival 700ms ease-out 180ms both;
  font-size: 21px;
}

.realtime-auth-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 1.3;
  min-width: 0;
}

.realtime-auth-copy small {
  color: #00796b;
  font-size: 0.69rem;
  font-weight: 800;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.realtime-auth-copy strong {
  color: #29434e;
  font-size: 0.88rem;
  font-weight: 700;
}

.realtime-auth-open,
.realtime-auth-sound,
.realtime-auth-close {
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  transition: background-color 160ms ease, border-color 160ms ease,
    color 160ms ease, transform 160ms ease;
}

.realtime-auth-open {
  background: #00897b;
  border: 1px solid #00897b;
  color: #ffffff;
  gap: 5px;
  height: 36px;
  padding: 0 12px;
}

.realtime-auth-open i {
  font-size: 17px;
}

.realtime-auth-open:hover,
.realtime-auth-open:focus-visible {
  background: #00796b;
  border-color: #00796b;
  color: #ffffff;
  transform: translateY(-1px);
}

.realtime-auth-close {
  background: transparent;
  border: 1px solid transparent;
  color: #607d8b;
  flex: 0 0 32px;
  height: 30px;
  padding: 0;
  width: 32px;
}

.realtime-auth-sound {
  background: #f3f8f7;
  border: 1px solid #c5e5e0;
  color: #00796b;
  flex: 0 0 32px;
  height: 32px;
  padding: 0;
  width: 32px;
}

.realtime-auth-sound:hover,
.realtime-auth-sound:focus-visible {
  background: #e0f2f1;
  border-color: #80cbc4;
  color: #00695c;
}

.realtime-auth-sound i {
  font-size: 18px;
}

.realtime-auth-close:hover,
.realtime-auth-close:focus-visible {
  background: #f3f6f7;
  border-color: #d9e2e5;
  color: #d84315;
}

.realtime-auth-close i {
  font-size: 19px;
}

.realtime-notice-enter-active,
.realtime-notice-leave-active {
  transition: opacity 220ms ease, transform 280ms ease;
}

.realtime-notice-enter-from {
  opacity: 0;
  transform: translate(-50%, -12px);
}

.realtime-notice-leave-to {
  opacity: 0;
  transform: translate(-50%, -6px);
}

@keyframes realtime-bell-arrival {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(10deg);
  }
  50% {
    transform: rotate(-8deg);
  }
  75% {
    transform: rotate(4deg);
  }
}

@media (max-width: 600px) {
  .realtime-auth-notice {
    align-items: flex-start;
    gap: 9px;
    top: 72px;
  }

  .realtime-auth-icon {
    flex-basis: 36px;
    height: 36px;
  }

  .realtime-auth-open {
    align-self: center;
    padding: 0 9px;
  }

  .realtime-auth-open span {
    display: none;
  }

  .realtime-auth-close {
    align-self: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .realtime-auth-icon i {
    animation: none;
  }

  .realtime-auth-open,
  .realtime-auth-sound,
  .realtime-auth-close,
  .realtime-notice-enter-active,
  .realtime-notice-leave-active {
    transition: none;
  }
}

/* WHITE-LABEL: OPERATIONAL NOTICE */
.realtime-auth-notice {
  background: var(--ui-popover);
  border-color: var(--ui-border);
  border-radius: var(--ui-radius);
  box-shadow: var(--ui-shadow-overlay);
  color: var(--ui-popover-foreground);
  top: calc(var(--ui-header-height) + 1rem);
}
.realtime-auth-icon {
  background: color-mix(in srgb, var(--ui-primary) 10%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-primary) 24%, var(--ui-border));
  color: var(--ui-primary);
}
.realtime-auth-copy small { color: var(--ui-primary); }
.realtime-auth-copy strong { color: var(--ui-foreground); }
.realtime-auth-open { background: var(--ui-primary); border-color: var(--ui-primary); color: var(--ui-primary-foreground); }
.realtime-auth-open:hover, .realtime-auth-open:focus-visible { background: var(--ui-primary); border-color: var(--ui-primary); filter: brightness(0.94); }
.realtime-auth-sound { background: var(--ui-muted); border-color: var(--ui-border); color: var(--ui-foreground); }
.realtime-auth-close { color: var(--ui-muted-foreground); }
</style>
